"use client";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

import { CourseRun } from "@/common/courseRun/types";
import ExecEdEnrollButton from "@/components/ExecEd/components/ExecEdEnrollButton";
import CTALink from "@/components/CTA/ctaLink";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { ExecEdStickyCTATestResult } from "@/monarch/rules/execEdStickyCTA/types";
import { ExecEdDetailPageTestResult } from "@/monarch/rules/execEdDetailPageTest/types";

type Props = {
  links: { label: string; path: string }[];
  courseRun: CourseRun | null;
  requestMoreInfoButton?: { label: string; path: string } | null;
  marketingType?: string;
  enrollButtonLink: URL | string;
};
export default function ExecEdStickyNav({
  links,
  courseRun,
  requestMoreInfoButton,
  marketingType = undefined,
  enrollButtonLink,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [isNavOnTop, setIsNavOnTop] = useState(false);

  useEffect(() => {
    if (window.scrollY === ref.current?.offsetTop) {
      setIsNavOnTop(true);
    }
    const onScroll = () => {
      if (ref.current?.offsetTop) {
        const isTop = Math.abs(window.scrollY - ref.current?.offsetTop) < 36;
        setIsNavOnTop(isTop);
      }
    };
    // clean up code
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <MonarchDataConsumer<ExecEdDetailPageTestResult>
      experimentKey="useExecEdDetailPageTest"
      render={({ data }) => {
        if (data?.hideStickyNav || data?.hideStickyNav === undefined) {
          return <></>;
        }

        const bgColor = data?.bannerColorHex
          ? `${data?.bannerColorHex}`
          : "hsl(var(--primary))";
        const textColor = data?.bannerTextColorHex
          ? `${data?.bannerTextColorHex}`
          : "hsl(var(--primary-foreground))";
        return (
          <div
            className={`sticky top-0 w-screen ml-[calc(-50vw+50%)] z-50 shadow-xl`}
            ref={ref}
          >
            {isNavOnTop && courseRun?.title && (
              <div className={"bg-primary px-5"}>
                <h2 className="text-primary-foreground mx-auto py-2 text-sm font-bold leading-5 m-0">
                  {courseRun.title}
                </h2>
              </div>
            )}
            <div
              style={{ backgroundColor: bgColor }}
              className={`${isNavOnTop ? "block" : "hidden"} px-5 flex`}
            >
              <div
                className={`px-4 mx-auto flex flex-col lg:flex-row items-center lg:gap-6 max-w-7xl w-full justify-center lg:justify-between`}
              >
                <nav
                  style={{ color: textColor }}
                  className="hidden md:flex py-4 mx-1 flex-wrap gap-x-10  underline underline-offset-4"
                >
                  {links.map((link) => (
                    <a href={link.path} key={`lg-${link.path}`}>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="flex flex-row gap-7 my-2 w-full md:w-auto">
                  {isNavOnTop && marketingType === "sprint" && courseRun && (
                    <ExecEdEnrollButton
                      courseRun={courseRun}
                      enrollButtonLink={enrollButtonLink}
                      onStickyNav={true}
                    />
                  )}
                  {isNavOnTop && requestMoreInfoButton && (
                    <CTALink
                      label={requestMoreInfoButton.label}
                      url={requestMoreInfoButton.path}
                      type="brand"
                      enable_tracking={true}
                      className="inline-block text-base w-full md:w-auto sm:min-w-32 py-2 px-4 min-h-11 whitespace-nowrap"
                      location="sticky-banner"
                      html_id="request-more-info-cta"
                      name="sticky-cta"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}
